import { roundUnitPrice, UNIT_PRICE_MAX_DECIMALS } from './unitPrice';

// Mirrors backend MoneyBounds.java. The margin bound is in PERCENT here and a FRACTION there
// (500 vs 5.0) because margin inputs are entered as a percentage and divided by 100 before the wire.
export const MIN_PRICE = 0;
export const MAX_PRICE = 1_000_000;
export const MIN_POSITIVE_PRICE = 0.01;

export const MIN_MARGIN_PERCENT = 0;
export const MAX_MARGIN_PERCENT = 500;

export const PRICE_MESSAGE_KEY = 'general:money_bounds.price';
export const MARGIN_MESSAGE_KEY = 'general:money_bounds.margin';

export const priceMessageParams = {
  min: MIN_PRICE.toLocaleString('en-US'),
  max: MAX_PRICE.toLocaleString('en-US'),
};

export const positivePriceMessageParams = {
  min: MIN_POSITIVE_PRICE.toLocaleString('en-US'),
  max: MAX_PRICE.toLocaleString('en-US'),
};

export const marginMessageParams = {
  min: MIN_MARGIN_PERCENT.toLocaleString('en-US'),
  max: MAX_MARGIN_PERCENT.toLocaleString('en-US'),
};

const isOutOfBounds = (
  value: number | null | undefined,
  min: number,
  max: number
) =>
  value != null &&
  !Number.isNaN(value) &&
  (!Number.isFinite(value) || value < min || value > max);

export const isPriceOutOfBounds = (value: number | null | undefined) =>
  isOutOfBounds(value, MIN_PRICE, MAX_PRICE);

export const isMarginPercentOutOfBounds = (value: number | null | undefined) =>
  isOutOfBounds(value, MIN_MARGIN_PERCENT, MAX_MARGIN_PERCENT);

export type ParsedPrice = { valid: boolean; amount?: number };

export function parsePriceInput(value: string, maxDecimals = 2): ParsedPrice {
  const trimmed = value.trim();
  if (trimmed === '') return { valid: true };
  const amount = Number(trimmed);
  const factor = 10 ** maxDecimals;
  const valid =
    Number.isFinite(amount) &&
    amount >= MIN_POSITIVE_PRICE &&
    !isPriceOutOfBounds(amount) &&
    Math.round(amount * factor) / factor === amount;
  return valid ? { valid, amount } : { valid };
}

// Valid only when the amount survives the display rounding, so shown price x quantity equals the line total.
export function parseUnitPriceInput(value: string): ParsedPrice {
  const parsed = parsePriceInput(value, UNIT_PRICE_MAX_DECIMALS);
  return parsed.amount !== undefined &&
    roundUnitPrice(parsed.amount) !== parsed.amount
    ? { valid: false }
    : parsed;
}
